<template>
  <div>
    <div
      class="page-community"
      :class="{ dark: darkMode }"
    >
      <HeaderImage
        :image="page.image"
        :image-mobile="page.imageMobile"
        :alt="page.title"
        class="background-image"
      />
      <div class="layer-container">
        <div class="layer">
          <div class="top-content">
            <div class="col">
              <p class="game">{{ capitalize(page.game) }}</p>
            </div>
            <div
              v-if="page.pegi"
              class="col"
            >
              <img
                :src="`/icons/PEGI_${page.pegi}.svg`"
                :alt="`pegi ${page.pegi}`"
              />
            </div>
          </div>
          <h1 v-if="!page.subCategory">
            {{ capitalize(page.title) }} <b>{{ page.game }}</b>
          </h1>
          <h1
            v-else
            class="sub-category"
          >
            <span>{{ capitalize(page.title) }}</span>
            {{ capitalize(page.subCategory) }}
          </h1>
          <p class="subtitle">{{ capitalize(page.description) }}</p>
          <ButtonPoint
            :points="page.contribPoints"
            :label="actionLabel"
            class="button-point-item"
            @click="openContribution"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { checkLoginModal } from '~/modals/checkLogin'
import {
  contributionModal,
  setContributionModalTab,
} from '~/modals/contribution'
import type { CommunityPostInfo } from '~/types'

const props = withDefaults(
  defineProps<{
    page: CommunityPostInfo
    darkMode?: boolean
    actionLabel?: string
    review?: boolean
  }>(),
  {
    darkMode: false,
    actionLabel: 'Ajouter un contenu',
    review: false,
  },
)

const { authenticated } = useAuthenticated()

const openContribution = () => {
  if (props.review) {
    track('cta', {
      cta_name: 'Ajouter un avis',
    })
  } else {
    track('cta', {
      cta_name: 'Ajouter un contenu',
    })
  }
  // like the other "Ajouter un contenu" buttons: the form is sent to /secure,
  // without a session it ended in a 405 and a generic error
  if (!authenticated.value) {
    return checkLoginModal.open()
  }
  setContributionModalTab('review')
  return contributionModal.open()
}
</script>

<style lang="scss" scoped>
.page-community {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 90vh;
  max-height: 73rem;

  h1 {
    margin-top: 1.8rem;
    color: $white;
    font-family: SST;
    font-size: 4.6rem;
    font-weight: 300;
    line-height: 6.2rem;
    letter-spacing: 0;

    b {
      display: none;
    }

    span {
      display: block;
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.3rem;
      letter-spacing: 0.1rem;
      text-transform: uppercase;
      opacity: 0.5;
    }

    &.sub-category {
      font-size: 3.2rem;
      line-height: 4.1rem;
    }
  }

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    object-fit: cover;
    object-position: top;
  }

  &.dark {
    &::after {
      border-color: transparent transparent $darkBackground transparent;
    }
  }

  .layer-container {
    @media screen and (min-width: 2000px) {
      position: absolute;
      top: 0;
      left: 0;
      width: calc(50vw - 100rem + 56.6rem);
      height: 100%;
      background: rgb(#000, 0.6);
    }
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 56.6rem;
    height: 100%;
    padding: 14rem 8rem 0;
    background: rgb(#000, 0.6);

    @media screen and (max-width: 700px) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding: 0 2.5rem;
    }

    @media screen and (min-width: 2000px) {
      right: 0;
      left: auto;
      background: transparent;
    }

    .top-content {
      display: flex;
      justify-content: space-between;
      padding-bottom: 3rem;
      border-bottom: 1px solid rgba($white, 0.23);

      @media screen and (max-width: 700px) {
        width: 100%;
      }

      .col {
        p {
          color: rgba($white, 0.47);
          font-size: 1.8rem;
          line-height: 2.6rem;
        }

        img {
          width: 3.656rem;
          height: 4.6rem;
          object-fit: contain;
        }
      }
    }
  }

  .subtitle {
    max-width: 37.4rem;
    margin-top: 2rem;
    color: $white;
    font-size: 1.2rem;
    line-height: 1.6rem;
  }

  .button-point-item {
    position: absolute;
    bottom: 14rem;
    left: 8rem;

    @media screen and (max-width: 700px) {
      position: relative;
      bottom: auto;
      left: auto;
      margin-top: 5rem;
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 0;
    height: 0;
    border-width: 0 0 5rem 100vw;
    border-style: solid;
    border-color: transparent transparent $white transparent;
  }
}
</style>
